/* ============================================================
   Warehouse context — login-resolved warehouse for the Supply module
   ------------------------------------------------------------
   The Supply screens identify a warehouse by its `pharmacyCode`
   (a string, e.g. "POD004795"), which the inventory / sales /
   stock endpoints expect as the warehouse id.

   At login we match the user against `warehouse/list` on
   stateCode + circleCode and persist the result to localStorage
   (see Login.jsx). These helpers read it back so the Supply
   screens never re-resolve or read localStorage ad hoc.

   Role behaviour mirrors the Orders module:
     - PH     -> locked to their resolved warehouse (no picker)
     - PHHEAD -> may switch warehouse, ONCE, from the topbar

   That switch is the "active warehouse" below: a single app-wide selection every
   screen and service reads, rather than a picker per screen.
   ============================================================ */
import authenticationServices from "./authenticationServices";

/* Find the warehouse row that belongs to a user. State is part of
   the key because circleCode "N" is shared across states, so circle
   alone is ambiguous — state + circle is unique. */
export function resolveWarehouseFromList(list, { state, circleCode } = {}) {
  const circle = circleCode || "N";
  return (
    (list || []).find(
      (w) => w.stateCode === state && w.circleCode === circle
    ) || null
  );
}

/* Persist a resolved warehouse/list row to localStorage. Shared by login
   and the on-demand resolver so the stored shape stays identical. */
export function storeResolvedWarehouse(wh) {
  if (!wh) return;
  localStorage.setItem("warehousePharmacyCode", wh.pharmacyCode);
  localStorage.setItem("warehouseId", String(wh.id));
  localStorage.setItem(
    "warehouseInfo",
    JSON.stringify({
      pharmacyCode: wh.pharmacyCode,
      pharmacyName: wh.pharmacyName,
      address: wh.address,
      cityName: wh.cityName,
      pincode: wh.pincode,
      stateCode: wh.stateCode,
      stateName: wh.stateName,
      dl: wh.dl,
      gstin: wh.gstin,
      pan: wh.pan,
    })
  );
}

/* Return the resolved warehouse pharmacyCode, resolving + persisting it on the
   fly if it isn't in localStorage yet (e.g. a session that signed in before
   warehouse resolution shipped, or any gap). Async — Supply screens call this
   on mount and fall back to getResolvedWarehouseCode() for the sync path. */
export async function ensureResolvedWarehouseCode() {
  const existing = localStorage.getItem("warehousePharmacyCode") || "";
  if (existing) return existing;
  try {
    const res = await authenticationServices.getwareHouseDetails();
    const list = res?.data?.successList || [];
    const wh = resolveWarehouseFromList(list, {
      state: localStorage.getItem("userState"),
      circleCode: localStorage.getItem("circleCode"),
    });
    if (wh) {
      storeResolvedWarehouse(wh);
      return wh.pharmacyCode;
    }
  } catch {
    /* leave unresolved; caller falls back to the dropdown / empty state */
  }
  return "";
}

/* ------------------------------------------------------------
   The ACTIVE warehouse — one choice, every screen
   ------------------------------------------------------------
   A pharmacy head oversees several stores but works in one at a time. That used
   to be a piece of state on each screen, so switching store meant re-picking it
   on Inventory, then on Racks, then on each report — and nothing stopped two
   screens from disagreeing about which store you were in.

   Now there is one selection, made in the topbar, persisted, and read from here.
   `getResolvedWarehouseCode()` deliberately returns it, because every caller —
   screens and services alike — wants "the store I am working in", not "the store
   my login resolved to". The login-resolved code remains the default and the
   fallback, and is still what a PH user is pinned to.
   ------------------------------------------------------------ */
const ACTIVE_CODE_KEY = "activeWarehouseCode";
const ACTIVE_INFO_KEY = "activeWarehouseInfo";

/* "All stores" — a head reporting across the estate rather than working in one
   store. Held as a sentinel rather than an empty string because the two mean
   different things: ALL is a deliberate choice, "" is "nobody has chosen yet",
   and a screen that needs one store has to tell them apart to say the right
   thing. getResolvedWarehouseCode() maps ALL to "", which is exactly what the
   endpoints want — omitting warehouseId means every warehouse. */
export const ALL_STORES = "__ALL__";

/* In-tab subscribers (the React provider). localStorage fires no event in the
   tab that wrote it, so changes are broadcast here. */
const listeners = new Set();
const notify = (code) => listeners.forEach((fn) => fn(code));

export function subscribeActiveWarehouse(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* The store the user's own login resolved to — their identity, not their current
   view. Only the fallback below and the PH lock care about the difference. */
export function getLoginWarehouseCode() {
  return localStorage.getItem("warehousePharmacyCode") || "";
}

/* Switch the active store. `wh` is a warehouse row ({ id, name, … } from
   supplyService.getWarehouses, or a raw warehouse/list row); null clears the
   override and falls back to the login-resolved store.

   Only a head may switch: a PH user is scoped to their own store server-side too,
   so letting the value drift would produce empty screens rather than another
   store's data — better to ignore it here. */
export function setActiveWarehouse(wh) {
  if (!isPharmacyHead()) return getLoginWarehouseCode();
  /* "All stores" carries no store detail — no code, and no info for an invoice
     header, which is why billing and inward refuse it. */
  if (wh === ALL_STORES) {
    localStorage.setItem(ACTIVE_CODE_KEY, ALL_STORES);
    localStorage.removeItem(ACTIVE_INFO_KEY);
    notify(ALL_STORES);
    return ALL_STORES;
  }
  if (!wh) {
    localStorage.removeItem(ACTIVE_CODE_KEY);
    localStorage.removeItem(ACTIVE_INFO_KEY);
    notify(getLoginWarehouseCode());
    return getLoginWarehouseCode();
  }
  const code = wh.id || wh.pharmacyCode || "";
  if (!code) return getResolvedWarehouseCode();
  localStorage.setItem(ACTIVE_CODE_KEY, code);
  /* The same shape login writes, so getWarehouseInfo() below can serve either.
     Carried because the invoice header (printInvoice.js) prints the store's DL /
     GSTIN / PAN — a head who switched store must not print their login store's
     registration numbers on another store's bill. */
  localStorage.setItem(
    ACTIVE_INFO_KEY,
    JSON.stringify({
      pharmacyCode: code,
      pharmacyName: wh.name || wh.pharmacyName || "",
      address: wh.address || "",
      cityName: wh.cityName || "",
      pincode: wh.pincode || "",
      stateCode: wh.stateCode || "",
      stateName: wh.stateName || "",
      dl: wh.dl || "",
      gstin: wh.gstin || "",
      pan: wh.pan || "",
    })
  );
  notify(code);
  return code;
}

/* The warehouse every Supply endpoint should be scoped to: the active store if a
   head has chosen one, otherwise the login-resolved store. Empty string when
   neither is known (a head who has not chosen yet). */
export function getResolvedWarehouseCode() {
  /* A stale override from a previous session as a head must not scope a PH
     user — the role is what decides, so it is checked on every read. */
  if (isPharmacyHead()) {
    const active = localStorage.getItem(ACTIVE_CODE_KEY);
    /* ALL becomes "" so every caller omits warehouseId and gets all warehouses;
       callers that need one store check isAllStores() and say so. */
    if (active === ALL_STORES) return "";
    if (active) return active;
    /* NO fallback to the login warehouse for a head. Login resolves a warehouse
       for everyone from state + circle, but for a head that is an accident of
       where they happen to be registered, not a decision about what they are
       looking at — and returning it here made "nothing chosen" indistinguishable
       from "this store chosen". Every screen then showed one store's figures as if
       they were the answer, which is the exact trap the old per-screen
       `isPharmacyHead() ? "" : …` guards existed to avoid. Empty means unchosen;
       screens say so and the topbar shows "Select a store". */
    return "";
  }
  return getLoginWarehouseCode();
}

/* True when the head has deliberately selected every store. Distinct from an
   empty code, which means nothing has been selected at all. */
export function isAllStores() {
  return isPharmacyHead() && localStorage.getItem(ACTIVE_CODE_KEY) === ALL_STORES;
}

/* Full pharmacy detail for the warehouse in scope (name, address, dl, gstin,
   pan, …) — used for the invoice header. Follows the active selection for the
   same reason the code does. */
export function getWarehouseInfo() {
  const read = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key)) || null;
    } catch {
      return null;
    }
  };
  if (isPharmacyHead()) {
    const active = read(ACTIVE_INFO_KEY);
    /* Only trust it while it still describes the active code — a half-updated
       pair would print one store's name against another's GSTIN. */
    if (active && active.pharmacyCode === getResolvedWarehouseCode()) return active;
  }
  return read("warehouseInfo");
}

/* The user's pharmacy role object (PHHEAD or PH), same lookup the
   Orders module uses. */
export function getPharmacyRole() {
  try {
    const user = JSON.parse(localStorage.getItem("loginObj"));
    return (
      user?.userRolesList?.find(
        (r) => r.roleCode === "PHHEAD" || r.roleCode === "PH"
      ) || null
    );
  } catch {
    return null;
  }
}

/* Pharmacy heads may switch warehouse; plain PH users are locked. */
export function isPharmacyHead() {
  return getPharmacyRole()?.roleCode === "PHHEAD";
}

/* Who may act on master-data decisions — today that's the Medicine Request
   approval queue (approve / reject). PHHEAD is the app's only admin role, so it
   stands in for the master-data team; if a dedicated role is added later, widen
   this single helper instead of each screen and the nav. */
export function isMasterDataAdmin() {
  return isPharmacyHead();
}

/* Display name of the signed-in user, for audit headers (X-User -> the backend
   records it as createdBy / sentBy / approvedBy / cancelledBy). Prefers the
   person's name, then the login code, and finally "system" so a call never
   goes out header-less. */
export function getCurrentUserName() {
  try {
    const user = JSON.parse(localStorage.getItem("loginObj"));
    const full = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
    if (full) return full;
    if (user?.name) return user.name;
  } catch {
    /* fall through to the localStorage fallbacks below */
  }
  return localStorage.getItem("access_name") || localStorage.getItem("userCode") || "system";
}

/* The signed-in user's mobile number, normalised to the bare 10 digits the
   backend stores ("+91 98765 43210" -> "9876543210"). "" when the login record
   carried none. Used to prefill "who is asking" contact fields — the medicine
   request's requesterNumber is mandatory, and making the operator retype a
   number the session already knows is how that field ends up empty.

   `mobile` is the field the login DTO defines and the profile menu reads, but the
   alternates are checked too: loginObj is whatever the environment's login API
   returned, and a session created before a backend rename still sits in
   localStorage. A miss is logged once — a silently blank required field is the
   harder bug to explain. */
const MOBILE_KEYS = ["mobile", "mobileNo", "mobileNumber", "phone", "phoneNo", "contactNumber"];
let warnedNoMobile = false;

export function getCurrentUserMobile() {
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("loginObj"));
  } catch {
    return "";
  }
  for (const key of MOBILE_KEYS) {
    const digits = String(user?.[key] ?? "").replace(/\D/g, "");
    /* Trim a leading +91 / 0 that some profile records carry. */
    if (digits) return digits.length > 10 ? digits.slice(-10) : digits;
  }
  if (!warnedNoMobile) {
    warnedNoMobile = true;
    console.warn(
      "[warehouseContext] The signed-in user has no mobile number on their login record " +
        `(checked ${MOBILE_KEYS.join(", ")}), so contact fields cannot be prefilled. ` +
        "loginObj keys:",
      user ? Object.keys(user) : user
    );
  }
  return "";
}

/* The signed-in user's email address, or "" when the login response carried
   none. Kept separate from getCurrentUserName() because the medicine-request
   endpoints read X-User BOTH as the audit user and as the requester's reply-to
   address — the backend only emails a decision back when the original request's
   X-User contained an "@". Callers that want that mail send this. */
export function getCurrentUserEmail() {
  const candidates = [];
  try {
    const user = JSON.parse(localStorage.getItem("loginObj"));
    if (user?.email) candidates.push(String(user.email));
  } catch {
    /* fall through to the localStorage copy below */
  }
  /* Login.jsx stores user.email verbatim, so an absent email lands here as the
     literal string "undefined" — the "@" test below filters that out too. */
  candidates.push(localStorage.getItem("userEmail") || "");
  return candidates.find((v) => v.includes("@")) || "";
}
